import { useEffect, useRef } from 'react'

const hasRenderableVideoTrack = (stream) => {
  if (!stream?.getVideoTracks) return false
  return stream.getVideoTracks().some((track) => track && track.readyState !== 'ended')
}

const hasRenderableAudioTrack = (stream) => {
  if (!stream?.getAudioTracks) return false
  return stream.getAudioTracks().some((track) => track && track.readyState !== 'ended')
}

const getStreamTrackSignature = (stream) => {
  if (!stream?.getTracks) return 'no-stream'

  return stream.getTracks()
    .map((track) => `${track.kind}:${track.id}:${track.readyState}:${track.enabled ? '1' : '0'}`)
    .sort()
    .join('|')
}

export default function Participant({
  participant,
  stream,
  videoTrack,
  audioTrack,
  isLocal = false,
  highlighted = false,
  layoutClass = '',
  tileStyle,
  onStreamBound
}) {
  const videoRef = useRef(null)
  const audioRef = useRef(null)
  const hasVideoTrack = Boolean(videoTrack) || hasRenderableVideoTrack(stream)
  const hasAudioTrack = Boolean(audioTrack) || hasRenderableAudioTrack(stream)
  const trackSignature = getStreamTrackSignature(stream)

  useEffect(() => {
    const videoElement = videoRef.current
    if (!videoElement) return

    if (videoTrack) {
      videoTrack.attach(videoElement)
      onStreamBound?.({ participantId: participant?.id, kind: 'video', trackSignature: videoTrack.sid || videoTrack.mediaStreamTrack?.id || 'livekit-video' })
      videoElement.muted = true
      videoElement.defaultMuted = true
      videoElement.autoplay = true
      videoElement.playsInline = true

      const playPromise = videoElement.play?.()
      if (playPromise?.catch) {
        playPromise.catch((err) => {
          console.error('Error playing participant video:', err)
        })
      }

      return () => {
        videoTrack.detach(videoElement)
        if (videoElement.srcObject) {
          videoElement.srcObject = null
        }
      }
    }

    if (!hasVideoTrack || !stream) {
      if (videoElement.srcObject) {
        videoElement.srcObject = null
      }
      return
    }

    if (videoElement.srcObject !== stream) {
      videoElement.srcObject = stream
      onStreamBound?.({ participantId: participant?.id, kind: 'video', trackSignature })
    }

    videoElement.muted = true
    videoElement.defaultMuted = true
    videoElement.autoplay = true
    videoElement.playsInline = true

    const playPromise = videoElement.play?.()
    if (playPromise?.catch) {
      playPromise.catch((err) => {
        console.error('Error playing participant video:', err)
      })
    }
  }, [hasVideoTrack, onStreamBound, participant?.id, stream, trackSignature, videoTrack])

  useEffect(() => {
    const audioElement = audioRef.current
    if (!audioElement || isLocal) return

    if (audioTrack) {
      audioTrack.attach(audioElement)
      onStreamBound?.({ participantId: participant?.id, kind: 'audio', trackSignature: audioTrack.sid || audioTrack.mediaStreamTrack?.id || 'livekit-audio' })
      audioElement.autoplay = true
      audioElement.playsInline = true
      audioElement.muted = false
      audioElement.defaultMuted = false

      const playPromise = audioElement.play?.()
      if (playPromise?.catch) {
        playPromise.catch((err) => {
          console.error('Error playing participant audio:', err)
        })
      }

      return () => {
        audioTrack.detach(audioElement)
        if (audioElement.srcObject) {
          audioElement.srcObject = null
        }
      }
    }

    if (!hasAudioTrack || !stream) {
      if (audioElement.srcObject) {
        audioElement.srcObject = null
      }
      return
    }

    if (audioElement.srcObject !== stream) {
      audioElement.srcObject = stream
      onStreamBound?.({ participantId: participant?.id, kind: 'audio', trackSignature })
    }

    audioElement.autoplay = true
    audioElement.playsInline = true
    audioElement.muted = false
    audioElement.defaultMuted = false

    const playPromise = audioElement.play?.()
    if (playPromise?.catch) {
      playPromise.catch((err) => {
        console.error('Error playing participant audio:', err)
      })
    }
  }, [audioTrack, hasAudioTrack, isLocal, onStreamBound, participant?.id, stream, trackSignature])

  return (
    <div
      style={tileStyle}
      className={`relative h-full min-h-[118px] overflow-hidden rounded-[18px] border bg-slate-950 sm:min-h-[180px] sm:rounded-[22px] ${layoutClass} ${highlighted ? 'border-amber-300/60 shadow-[0_0_0_1px_rgba(251,191,36,0.35)]' : 'border-white/8'} ${hasVideoTrack ? 'bg-slate-950' : 'bg-[radial-gradient(circle_at_top,_rgba(148,163,184,0.18),_transparent_42%),linear-gradient(180deg,#111827_0%,#020617_100%)]'}`}
    >
      {hasVideoTrack ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center px-4 text-center">
          <div className={`flex h-20 w-20 items-center justify-center rounded-full text-2xl font-bold ${highlighted ? 'bg-amber-300 text-slate-950' : 'bg-slate-800 text-slate-100'}`}>
            {participant?.initials || 'RD'}
          </div>
          <p className="mt-4 text-base font-semibold text-white">{participant?.name || 'Participante'}</p>
          <p className="mt-1 text-xs text-white/55">
            {isLocal ? 'Tu camara esta lista para compartirse' : hasAudioTrack ? 'Compartiendo audio sin video' : 'Esperando video o audio de este participante'}
          </p>
        </div>
      )}

      {!isLocal && <audio ref={audioRef} autoPlay className="hidden" />}

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-slate-950/85 to-transparent sm:h-20" />
      <div className="absolute bottom-2 left-2 inline-flex max-w-[calc(100%-1rem)] items-center gap-1.5 rounded-full border border-white/10 bg-slate-950/42 px-2 py-1 text-[10px] text-white/90 backdrop-blur-md sm:bottom-3 sm:left-3 sm:gap-2 sm:px-2.5 sm:text-[11px]">
        <span>{participant?.name || 'Participante'}</span>
        {isLocal && <span className="rounded-full bg-sky-400/20 px-2 py-0.5 text-[10px] font-semibold text-sky-100">Tu camara</span>}
        {hasAudioTrack && !isLocal && <span className="rounded-full bg-emerald-400/20 px-2 py-0.5 text-[10px] font-semibold text-emerald-100">Audio</span>}
      </div>
    </div>
  )
}
